/**
 * SandataExportTab - Sandata EVV Export component
 * Extracted from EVVPage to reduce file size
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Download,
  RefreshCw,
  CheckCircle,
  CheckCircle2,
  AlertTriangle,
  FileText,
  FileJson,
  Info,
} from "lucide-react";

export function SandataExportTab({
  sandataExportStartDate,
  setSandataExportStartDate,
  sandataExportEndDate,
  setSandataExportEndDate,
  sandataIncludeClients,
  setSandataIncludeClients,
  sandataExporting,
  sandataExportResult,
  handleSandataExport,
  checkingCompliance,
  complianceReport,
  checkSandataCompliance,
  sandataReferenceData,
}) {
  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl border border-primary/20 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary/10 rounded-lg">
            <FileJson className="w-8 h-8 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-slate-900 mb-1">Sandata EVV Export</h2>
            <p className="text-slate-600">
              Export visit data in Sandata-compliant JSON format per Wisconsin DMS Third-Party EVV Addendum v2.5
            </p>
            <div className="flex items-center gap-4 mt-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                WI DMS Compliant
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                <Info className="w-3.5 h-3.5" />
                Version 2.5
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Export Controls */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-primary" />
          Export Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={sandataExportStartDate}
              onChange={(e) => setSandataExportStartDate(e.target.value)}
              data-testid="sandata-start-date"
            />
          </div>
          <div className="space-y-2">
            <Label>End Date</Label>
            <Input
              type="date"
              value={sandataExportEndDate}
              onChange={(e) => setSandataExportEndDate(e.target.value)}
              data-testid="sandata-end-date"
            />
          </div>
          <div className="space-y-2">
            <Label>Include Clients</Label>
            <div className="flex items-center gap-2 pt-2">
              <Switch
                checked={sandataIncludeClients}
                onCheckedChange={setSandataIncludeClients}
                id="include-clients"
              />
              <Label htmlFor="include-clients" className="text-sm cursor-pointer text-slate-600">
                Include client records in export
              </Label>
            </div>
          </div>
          <div className="flex items-end">
            <Button 
              onClick={handleSandataExport}
              disabled={sandataExporting}
              className="w-full gap-2"
              data-testid="sandata-export-btn"
            >
              {sandataExporting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export to Sandata
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Export Result */}
        {sandataExportResult && (
          <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-emerald-800">Export Successful</h4>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-emerald-600">Visits:</span>
                    <span className="ml-1 font-medium text-emerald-800">{sandataExportResult.metadata?.visit_count || 0}</span>
                  </div>
                  <div>
                    <span className="text-emerald-600">Clients:</span>
                    <span className="ml-1 font-medium text-emerald-800">{sandataExportResult.metadata?.client_count || 0}</span>
                  </div>
                  <div>
                    <span className="text-emerald-600">Format:</span>
                    <span className="ml-1 font-medium text-emerald-800">{sandataExportResult.metadata?.format_version}</span>
                  </div>
                  <div>
                    <span className="text-emerald-600">Date:</span>
                    <span className="ml-1 font-medium text-emerald-800">
                      {sandataExportResult.metadata?.export_date?.split('T')[0]}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compliance Check Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              Compliance Check
            </h3>
            <p className="text-sm text-slate-500">Validate clients and employees for Sandata requirements</p>
          </div>
          <Button
            onClick={checkSandataCompliance}
            disabled={checkingCompliance}
            variant="outline"
            className="gap-2"
            data-testid="sandata-compliance-check-btn"
          >
            {checkingCompliance ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Validate All
              </>
            )}
          </Button>
        </div>

        {/* Compliance Report */}
        {complianceReport && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`p-4 rounded-lg ${complianceReport.summary.clients_with_issues === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Clients</p>
                <p className={`text-2xl font-bold ${complianceReport.summary.clients_with_issues === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {complianceReport.summary.clients_ready}/{complianceReport.summary.total_clients}
                </p>
                <p className="text-xs text-slate-500">Ready for export</p>
              </div>
              <div className={`p-4 rounded-lg ${complianceReport.summary.employees_with_issues === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Employees</p>
                <p className={`text-2xl font-bold ${complianceReport.summary.employees_with_issues === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {complianceReport.summary.employees_ready}/{complianceReport.summary.total_employees}
                </p>
                <p className="text-xs text-slate-500">Ready for export</p>
              </div>
              <div className={`p-4 rounded-lg ${complianceReport.summary.clients_with_issues === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Client Issues</p>
                <p className={`text-2xl font-bold ${complianceReport.summary.clients_with_issues === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {complianceReport.summary.clients_with_issues}
                </p>
                <p className="text-xs text-slate-500">Missing Medicaid ID</p>
              </div>
              <div className={`p-4 rounded-lg ${complianceReport.summary.employees_with_issues === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Employee Issues</p>
                <p className={`text-2xl font-bold ${complianceReport.summary.employees_with_issues === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {complianceReport.summary.employees_with_issues}
                </p>
                <p className="text-xs text-slate-500">Missing Provider ID</p>
              </div>
            </div>

            {/* Overall Status */}
            {complianceReport.summary.overall_ready ? (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="font-semibold text-emerald-800">All Records Compliant</p>
                  <p className="text-sm text-emerald-600">Your agency is ready for Sandata EVV export</p>
                </div>
              </div>
            ) : (
              <>
                {/* Clients Missing Medicaid ID */}
                {complianceReport.clients_missing_medicaid?.length > 0 && (
                  <div className="border border-red-200 rounded-lg overflow-hidden">
                    <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                      <h4 className="font-medium text-red-800 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Clients Missing Medicaid ID ({complianceReport.clients_missing_medicaid.length})
                      </h4>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Issue</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {complianceReport.clients_missing_medicaid.map((client) => (
                            <TableRow key={client.id} className="bg-red-50/50">
                              <TableCell className="font-medium">{client.name}</TableCell>
                              <TableCell>
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                  {client.status}
                                </span>
                              </TableCell>
                              <TableCell className="text-red-600 text-sm">{client.issue}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-primary"
                                  onClick={() => window.open(`/clients/${client.id}`, '_blank')}
                                >
                                  Edit
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Clients with Invalid Medicaid ID */}
                {complianceReport.clients_invalid_medicaid?.length > 0 && (
                  <div className="border border-amber-200 rounded-lg overflow-hidden">
                    <div className="bg-amber-50 px-4 py-2 border-b border-amber-200">
                      <h4 className="font-medium text-amber-800 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Clients with Invalid Medicaid ID ({complianceReport.clients_invalid_medicaid.length})
                      </h4>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Current ID</TableHead>
                            <TableHead>Issue</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {complianceReport.clients_invalid_medicaid.map((client) => (
                            <TableRow key={client.id} className="bg-amber-50/50">
                              <TableCell className="font-medium">{client.name}</TableCell>
                              <TableCell className="font-mono text-sm">{client.medicaid_id}</TableCell>
                              <TableCell className="text-amber-600 text-sm">{client.issue}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-primary"
                                  onClick={() => window.open(`/clients/${client.id}`, '_blank')}
                                >
                                  Edit
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Employees Missing Provider ID */}
                {complianceReport.employees_missing_provider?.length > 0 && (
                  <div className="border border-amber-200 rounded-lg overflow-hidden">
                    <div className="bg-amber-50 px-4 py-2 border-b border-amber-200">
                      <h4 className="font-medium text-amber-800 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Employees Missing Provider ID ({complianceReport.employees_missing_provider.length})
                      </h4>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Issue</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {complianceReport.employees_missing_provider.map((emp) => (
                            <TableRow key={emp.id} className="bg-amber-50/50">
                              <TableCell className="font-medium">{emp.name}</TableCell>
                              <TableCell className="capitalize">{emp.role}</TableCell>
                              <TableCell className="text-amber-600 text-sm">{emp.issue}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-primary"
                                  onClick={() => window.open(`/employees/${emp.id}`, '_blank')}
                                >
                                  Edit
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* No report yet */}
        {!complianceReport && (
          <div className="text-center py-8 text-slate-500">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Click &quot;Validate All&quot; to check compliance status</p>
          </div>
        )}
      </div>

      {/* Reference Data */}
      {sandataReferenceData && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Sandata Reference Data
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Payers */}
            <div>
              <h4 className="font-medium text-slate-800 mb-2">Supported Payers</h4>
              <div className="space-y-1">
                {sandataReferenceData.payers && Object.entries(sandataReferenceData.payers).map(([key, val]) => (
                  <div key={key} className="text-sm px-2 py-1 bg-slate-50 rounded flex justify-between">
                    <span className="text-slate-600">{val.payer_id}</span>
                    <span className="text-slate-500 text-xs">{val.program_id}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Service Codes */}
            <div>
              <h4 className="font-medium text-slate-800 mb-2">Service Codes (HCPCS)</h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {sandataReferenceData.service_codes && Object.entries(sandataReferenceData.service_codes).map(([code, desc]) => (
                  <div key={code} className="text-sm px-2 py-1 bg-slate-50 rounded">
                    <span className="font-mono text-primary">{code}</span>
                    <span className="ml-2 text-slate-600 text-xs">{desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Call Types */}
            <div>
              <h4 className="font-medium text-slate-800 mb-2">Supported Call Types</h4>
              <div className="flex flex-wrap gap-2">
                {sandataReferenceData.call_types?.map((type) => (
                  <span key={type} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">
                    {type}
                  </span>
                ))}
              </div>
              
              <h4 className="font-medium text-slate-800 mt-4 mb-2">Time Zones</h4>
              <div className="flex flex-wrap gap-2">
                {sandataReferenceData.timezones?.map((tz) => (
                  <span key={tz} className="px-2 py-1 bg-amber-50 text-amber-700 rounded text-sm">
                    {tz}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Notes */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-amber-800 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Compliance Requirements
        </h3>
        <ul className="space-y-2 text-sm text-amber-700">
          <li className="flex items-start gap-2">
            <span className="text-amber-500">•</span>
            Client Medicaid IDs must be 10-12 digits for valid export
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500">•</span>
            Employee Provider IDs must be 9-15 digits (numeric)
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500">•</span>
            GPS coordinates are required for Mobile call types
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500">•</span>
            Only completed visits with clock-out times are included in exports
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500">•</span>
            All timestamps are converted to UTC (ISO 8601 format)
          </li>
        </ul>
      </div>
    </div>
  );
}
